import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener, NgZone, OnDestroy, ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { AgentEmbedAttachHandle, AgentEmbedService } from 'shared/services/agent-embed/agent-embed.service';
import {
  AgentWindowState, AgentWindowStateService, PanelGeometry, clampGeometry,
} from 'shared/services/agent-window-state/agent-window-state.service';

/** `setPointerCapture` can throw (`NotFoundError`/`InvalidPointerId`) when the browser has no
 * "active" pointer for the given id to capture -- observed for synthetic pointer events (unit
 * tests dispatching a non-primary pointerId) and, in real usage, for touch input whose pointerId
 * is never 1 (each active touch gets its own id, unlike the conventional primary-mouse id 1).
 * Capture is a nice-to-have (keeps drag/resize tracking even if the pointer leaves the handle
 * element); losing it must never abort the gesture -- the window-level move/up/cancel listeners in
 * beginPointerTracking() below do the actual tracking regardless (mirrors eStream's own
 * tryCapturePointer, use-agent-chat-window.ts). BRN-2118 L6 remediation: the original BRAIN code
 * called setPointerCapture unguarded, so this exception -- uncaught -- aborted onHeaderPointerDown/
 * onResizeHandlePointerDown before beginPointerTracking ever ran, silently breaking the whole
 * gesture for any non-primary pointerId. */
function tryCapturePointer(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Intentionally swallowed -- see doc comment above.
  }
}

// BRN-2118 (AC1, AC9): the floating chat panel -- replaces the (retired) route-blocking MatDialog
// modal per lifecycle/decisions/floating-chat-entry-point.md. No backdrop (the host page stays
// interactive); closes via X or Esc (only while focus is inside the panel); detach/drag/resize/
// reattach per AC9, all geometry owned by AgentWindowStateService. The iframe embed/sandbox/
// postMessage-bridge wiring is unchanged from the original design -- only this presentation shell
// is new.
//
// BRN-2118 L6 remediation (Cluster A1): #chatFrame/#panelRoot live under *ngIf="state.isOpen" and
// this component mounts once at the app-shell root, so a component-lifecycle hook
// (ngAfterViewInit/ngOnInit, each fires exactly once) can never react to a LATER open. @ViewChild
// SETTERS fire every time Angular's query result changes -- i.e. exactly when *ngIf creates/
// destroys the element -- so they are the correct hook for "attach the bridge + focus the panel on
// open, detach + restore focus on close, re-attach on reopen." This supersedes the old
// ngAfterViewInit attach and the declared-but-never-assigned `stateSubscription` field (deleted --
// the setter is the reactive mechanism now, so there is nothing left to subscribe to `state$` for).
@Component({
  selector: 'app-agent-chat-panel',
  templateUrl: './agent-chat-panel.component.html',
  styleUrls: ['./agent-chat-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentChatPanelComponent implements OnDestroy {
  readonly state$: Observable<AgentWindowState> = this.windowState.state$;

  /** Computed ONCE in the constructor (the embed URL is a static, environment-derived constant) --
   * never a per-change-detection getter. Angular's [src] dirty-check compares the SafeResourceUrl
   * *object* returned by bypassSecurityTrustResourceUrl, not the resolved URL string, so a fresh
   * object per CD pass was dirty every pass and re-navigated the iframe continuously while dragging
   * / on every timer tick (BRN-2118 L6 remediation, Cluster A2 -- reload storm). */
  readonly embedUrlSafe: SafeResourceUrl;

  /** Cached at construction, refreshed only on window:resize (Cluster F2) -- previously a template
   * getter that re-read window.innerWidth on every change-detection pass. */
  smallScreen: boolean;

  private _panelRootRef?: ElementRef<HTMLElement>;
  @ViewChild('panelRoot')
  set panelRootRef(ref: ElementRef<HTMLElement> | undefined) {
    this._panelRootRef = ref;
    if (ref) {
      this.lastFocusedElement = (typeof document !== 'undefined' ? document.activeElement : null) as HTMLElement | null;
      ref.nativeElement.focus();
    } else {
      this.lastFocusedElement?.focus?.();
      this.lastFocusedElement = null;
    }
  }
  get panelRootRef(): ElementRef<HTMLElement> | undefined {
    return this._panelRootRef;
  }

  private _chatFrameRef?: ElementRef<HTMLIFrameElement>;
  private embedHandle: AgentEmbedAttachHandle | null = null;
  @ViewChild('chatFrame')
  set chatFrameRef(ref: ElementRef<HTMLIFrameElement> | undefined) {
    this._chatFrameRef = ref;
    if (ref) {
      this.embedHandle = this.agentEmbed.attachIframe(ref.nativeElement, {
        onClose: () => this.close(),
      });
    } else {
      this.embedHandle?.detach();
      this.embedHandle = null;
    }
  }
  get chatFrameRef(): ElementRef<HTMLIFrameElement> | undefined {
    return this._chatFrameRef;
  }

  private lastFocusedElement: HTMLElement | null = null;

  // Gesture-active state is `dragStart`/`resizeStart` !== null; the pointerId itself only needs to
  // live in the beginPointerTracking() closure below (it is never read back on the component).
  private dragStart: { pointerX: number; pointerY: number; geometry: PanelGeometry } | null = null;
  private dragLiveGeometry: PanelGeometry | null = null;
  private dragStopTracking: (() => void) | null = null;

  private resizeStart: { pointerX: number; pointerY: number; geometry: PanelGeometry; isDetached: boolean } | null = null;
  private resizeLiveGeometry: PanelGeometry | null = null;
  private resizeStopTracking: (() => void) | null = null;

  constructor(
    private readonly agentEmbed: AgentEmbedService,
    private readonly windowState: AgentWindowStateService,
    private readonly sanitizer: DomSanitizer,
    private readonly zone: NgZone,
  ) {
    this.embedUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.agentEmbed.embedUrl);
    this.smallScreen = this.windowState.smallScreen;
  }

  ngOnDestroy(): void {
    this.dragStopTracking?.();
    this.resizeStopTracking?.();
    this.embedHandle?.detach();
    this.lastFocusedElement?.focus?.();
  }

  /** Bound to the iframe's native (load) event -- clears the Cluster G load-failure watchdog. */
  onIframeLoad(): void {
    this.embedHandle?.notifyLoaded();
  }

  /** Bound to the iframe's native (error) event -- treated identically to a load timeout. */
  onIframeError(): void {
    this.embedHandle?.notifyError();
  }

  close(): void {
    this.windowState.close();
  }

  detach(): void {
    this.windowState.detach();
  }

  reattach(): void {
    this.windowState.reattach();
  }

  toggleFullscreen(): void {
    this.windowState.toggleFullscreen();
  }

  /**
   * True when the panel occupies the whole viewport and its stored geometry must NOT be applied as
   * inline width/height/left/top -- either because the viewport is too small for a windowed panel
   * (automatic) or because the user asked for full screen (explicit). Keeps the template from
   * repeating the same two-part condition on four style bindings.
   */
  get fillsViewport(): boolean {
    return this.smallScreen || this.windowState.value.isFullscreen;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    const root = this.panelRootRef?.nativeElement;
    if (!root || !document.activeElement || !root.contains(document.activeElement)) {
      return;
    }
    event.stopPropagation();
    this.close();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.smallScreen = this.windowState.smallScreen;
    this.windowState.reclampToViewport();
  }

  // BRN-2118 L6 remediation (Cluster F2, F1): pointerdown stays a template (in-zone) binding -- one
  // event per gesture, negligible cost. The high-frequency pointermove/pointerup/pointercancel trio
  // is registered programmatically, OUTSIDE the Angular zone, in beginPointerTracking() below --
  // dragging/resizing no longer triggers a full-app ApplicationRef.tick() per pointer event. The
  // live geometry is written directly to the panel's native style (clamped with the same pure
  // clampGeometry the service uses, so on-screen clamping during the gesture is unchanged) and
  // committed through AgentWindowStateService.updateGeometry exactly once, when the gesture ends --
  // the "explicit re-entry for state commits" the remediation worklist calls for. Because the
  // service is never called mid-drag, there is also no per-pointermove sessionStorage write to
  // defer (Cluster F1 falls out of this design rather than needing a separate persist:false dance).
  onHeaderPointerDown(event: PointerEvent, isDetached: boolean, geometry: PanelGeometry): void {
    // fillsViewport also covers fullscreen: a panel pinned to the viewport has nowhere to be
    // dragged to, and letting a drag start would write a geometry the user never sees applied.
    if (!isDetached || this.fillsViewport) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      return; // never start a drag from the header's own action buttons.
    }
    this.dragStart = { pointerX: event.clientX, pointerY: event.clientY, geometry };
    tryCapturePointer(event.currentTarget as HTMLElement, event.pointerId);
    this.dragStopTracking?.();
    this.dragStopTracking = this.beginPointerTracking(
      event.pointerId,
      (clientX, clientY) => this.applyLiveDragTransform(clientX, clientY),
      () => this.commitDrag(),
    );
  }

  onResizeHandlePointerDown(event: PointerEvent, geometry: PanelGeometry, isDetached: boolean): void {
    if (this.fillsViewport) {
      return;
    }
    event.stopPropagation();
    this.resizeStart = { pointerX: event.clientX, pointerY: event.clientY, geometry, isDetached };
    tryCapturePointer(event.currentTarget as HTMLElement, event.pointerId);
    this.resizeStopTracking?.();
    this.resizeStopTracking = this.beginPointerTracking(
      event.pointerId,
      (clientX, clientY) => this.applyLiveResizeTransform(clientX, clientY),
      () => this.commitResize(),
    );
  }

  /** Registers window-level pointermove/pointerup/pointercancel listeners OUTSIDE the Angular zone
   * for the given pointerId: `onMove` fires live, outside the zone (no change detection); `onEnd`
   * fires exactly once on pointerup/pointercancel, with the zone re-entered around it. Returns a
   * teardown function that removes the listeners (idempotent). */
  private beginPointerTracking(
    pointerId: number,
    onMove: (clientX: number, clientY: number) => void,
    onEnd: () => void,
  ): () => void {
    let moveListener: ((event: PointerEvent) => void) | null = null;
    let endListener: ((event: PointerEvent) => void) | null = null;
    const stop = (): void => {
      if (moveListener) {
        window.removeEventListener('pointermove', moveListener);
        moveListener = null;
      }
      if (endListener) {
        window.removeEventListener('pointerup', endListener);
        window.removeEventListener('pointercancel', endListener);
        endListener = null;
      }
    };
    this.zone.runOutsideAngular(() => {
      moveListener = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) {
          return;
        }
        onMove(event.clientX, event.clientY);
      };
      endListener = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) {
          return;
        }
        stop();
        this.zone.run(() => onEnd());
      };
      window.addEventListener('pointermove', moveListener);
      window.addEventListener('pointerup', endListener);
      window.addEventListener('pointercancel', endListener);
    });
    return stop;
  }

  private applyLiveDragTransform(clientX: number, clientY: number): void {
    if (!this.dragStart) {
      return;
    }
    const deltaX = clientX - this.dragStart.pointerX;
    const deltaY = clientY - this.dragStart.pointerY;
    const candidate: PanelGeometry = {
      ...this.dragStart.geometry,
      x: this.dragStart.geometry.x + deltaX,
      y: this.dragStart.geometry.y + deltaY,
    };
    // Drag only ever runs while detached -- guarded in onHeaderPointerDown.
    const clamped = clampGeometry(candidate, 'detached', window.innerWidth, window.innerHeight);
    this.dragLiveGeometry = clamped;
    const el = this.panelRootRef?.nativeElement;
    if (el) {
      el.style.left = `${clamped.x}px`;
      el.style.top = `${clamped.y}px`;
    }
  }

  private commitDrag(): void {
    this.dragStopTracking = null;
    this.dragStart = null;
    const finalGeometry = this.dragLiveGeometry;
    this.dragLiveGeometry = null;
    if (finalGeometry) {
      this.windowState.updateGeometry(finalGeometry);
    }
  }

  private applyLiveResizeTransform(clientX: number, clientY: number): void {
    if (!this.resizeStart) {
      return;
    }
    const deltaX = clientX - this.resizeStart.pointerX;
    const deltaY = clientY - this.resizeStart.pointerY;
    const candidate: PanelGeometry = {
      ...this.resizeStart.geometry,
      width: this.resizeStart.geometry.width + deltaX,
      height: this.resizeStart.geometry.height + deltaY,
    };
    const mode = this.resizeStart.isDetached ? 'detached' : 'docked';
    const clamped = clampGeometry(candidate, mode, window.innerWidth, window.innerHeight);
    this.resizeLiveGeometry = clamped;
    const el = this.panelRootRef?.nativeElement;
    if (el) {
      el.style.width = `${clamped.width}px`;
      el.style.height = `${clamped.height}px`;
    }
  }

  private commitResize(): void {
    this.resizeStopTracking = null;
    this.resizeStart = null;
    const finalGeometry = this.resizeLiveGeometry;
    this.resizeLiveGeometry = null;
    if (finalGeometry) {
      this.windowState.updateGeometry(finalGeometry);
    }
  }
}
